// ─────────────────────────────────────────────
//  TeamDirectory — the department's people, surgeons and facilities, searched.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The body of the Team page, with no page chrome of its own, so that the web
//  page (TeamPage, which adds the lobby topbar) and the native shell (which
//  adds its own header and tab bar) render the SAME component. A lookalike
//  second copy for mobile is how two directories start disagreeing.
//
//  ⚠️ THREE SECTIONS, FROM THREE OF ATC'S OWN COLLECTIONS (owner, 2026-08-19:
//  "I want nothing public"). People from `staff`, split by their REAL role
//  because ATC owns the roster now; surgeons from `surgeons` with their
//  specialty; facilities from `facilities` with their address. The mirrored
//  Google-Sheet document this page used to read is retired.
//
//  ⚠️ A ROW IS A NAME, A PHONE AND AN EMAIL. Email is shown deliberately — the
//  owner reversed the earlier decision to hide it. What has NOT come back is
//  the profile join: no campus badge, no Message button, no link to anybody's
//  profile, and NOTHING here reads `users`. Everything a row prints comes from
//  the row's own document.
//
//  ⚠️ A PERSON WITH NO PHONE RENDERS WITHOUT A CALL LINK, AND A PERSON WITH NO
//  EMAIL WITHOUT AN EMAIL LINK — never `tel:null`, never an empty `mailto:`. A
//  link that looks live and does nothing is worse than a blank space, because
//  the person who taps it believes the call was placed.
//
//  ⚠️ SEARCH IS NOT A FEATURE OF THIS PAGE, IT IS THE PAGE. The field is the
//  first control under the heading, full width, always visible, never behind a
//  toggle, and the list below it starts COLLAPSED to section headings. A wall
//  of rows on first paint is slow on a phone and useless on any device.
//
//  ⚠️ AUTOFOCUS IS DESKTOP-ONLY. Focusing the field inside the native shell (or
//  on a phone browser) pops the keyboard over the content the instant the
//  screen opens, hiding the thing the person came to look at.
//
//  ⚠️ EVERY "NOTHING" GETS A SENTENCE. Nobody added yet, a read the rules
//  refused, and a query that matched nobody are three different situations and
//  each says which one it is, with a way out. A blank surface reads as a broken
//  app — the same rule MessagesPage's Notice follows.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTeamDirectory } from "../hooks/useTeamDirectory";
import { useIsMobile } from "../hooks/useIsMobile";
import { isNative } from "../native/platform";
// ⚠️ NO useStaff, NO useUsers, AND NOTHING ELSE THAT READS A LOGIN DOCUMENT.
// This import list is the guard: `users` carries push tokens, facility
// membership and status, and a contact list is not a reason to hand any of it
// to a colleague. The three collections this page needs arrive through the one
// hook below.
import {
  canSeeTeamDirectory, countLabel, directoryTotal, filterDirectory,
} from "../domain/teamDirectory";
import {
  CONTACT_SECTIONS, FILTERABLE_SECTION_IDS, buildContacts, mailtoHref, telHref,
} from "../domain/teamContacts";
import s from "./teamDirectory.styles";

// Typing is cheap; re-filtering the whole department on every keystroke is not.
// Short enough that the count never feels behind the fingers.
const DEBOUNCE_MS = 180;

// How many rows a section renders before offering "show all". Keeps the first
// paint after a broad query (a single letter matches hundreds) small without
// ever hiding anyone from the count or from a narrower query.
const PAGE = 40;

const SECTION_LABELS = Object.fromEntries(CONTACT_SECTIONS.map((x) => [x.id, x.label]));

function Notice({ title, body, ctaLabel, onCta }) {
  return (
    <div style={s.notice}>
      {/* An h2 rather than a styled paragraph: a notice REPLACES the sections,
          which are the page's other h2s, so the outline stays whole. */}
      <h2 style={s.noticeTitle}>{title}</h2>
      <p style={s.noticeBody}>{body}</p>
      {ctaLabel && (
        <button type="button" style={s.noticeCta} onClick={onCta}>{ctaLabel}</button>
      )}
    </div>
  );
}

// One contact. Reaching them is the point of the whole surface, so the phone
// and the email are the two things styled as actions and sized past the 44px
// touch minimum. `meta` is the one secondary fact this KIND of row carries — a
// surgeon's specialty, a facility's address — and is never alarm-coloured.
function ContactRow({ row }) {
  const tel = telHref(row.phone);
  const mail = mailtoHref(row.email);
  return (
    <li style={s.row}>
      <div style={s.rowMain}>
        <span style={s.rowName}>{row.name}</span>
        {row.meta && <span style={s.note}>{row.meta}</span>}
      </div>
      <div style={s.actions}>
        {tel ? (
          <a href={tel} className="atc-team-call" style={s.call} aria-label={`Call ${row.name}`}>
            <span aria-hidden="true">📞</span>{row.phoneText || row.phone}
          </a>
        ) : (
          // A plain statement of fact, in the same ink as everything else. Not
          // amber, not red — in this app alarm colour means an emergency.
          <span style={s.noPhone}>No number listed</span>
        )}
        {mail && (
          <a href={mail} className="atc-team-warm" style={s.mail} aria-label={`Email ${row.name}`}>
            <span aria-hidden="true">✉️</span>{row.email}
          </a>
        )}
      </div>
    </li>
  );
}

export default function TeamDirectory({ inShell = false }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const { staff, surgeons, facilities, loading, error } = useTeamDirectory();

  // `query` is what the field shows (every keystroke); `applied` is what the
  // list filters by (debounced). Two states on purpose — a debounced INPUT
  // value fights the person typing.
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState("");
  const [chips, setChips] = useState(() => new Set());
  const [opened, setOpened] = useState(() => new Set());
  const [closed, setClosed] = useState(() => new Set());
  const [limits, setLimits] = useState(() => ({}));
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setApplied(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Desktop only — see the header. `isNative()` covers the packaged app and the
  // ?forceNative QA override; the width check covers a phone browser.
  const narrow = useIsMobile();
  useEffect(() => {
    if (isNative() || narrow) return;
    inputRef.current?.focus();
  }, [narrow]);

  // The three collections → one array of rows, ONCE per snapshot. Unlisted
  // records, records outside this account's facility scope, and synthesized
  // email addresses are all dropped in here — see domain/teamContacts.
  const rows = useMemo(
    () => buildContacts({ staff, surgeons, facilities, auth }),
    [staff, surgeons, facilities, auth]
  );

  const searching = applied.trim().length > 0 || chips.size > 0;
  const { sections, shown } = useMemo(
    () => filterDirectory(rows, { query: applied, sections: chips }),
    [rows, applied, chips]
  );
  const total = directoryTotal(rows);

  // A section is open when the person opened it, or — while a search is running
  // — whenever they have not deliberately closed it. Searching then expands
  // matches on its own, which is the only behaviour that makes the collapsed
  // first paint safe: nothing is ever hidden FROM the search.
  const isOpen = (id) => (searching ? !closed.has(id) : opened.has(id));
  const toggleSection = (id) => {
    const open = isOpen(id);
    const flip = (set, add) => {
      const next = new Set(set);
      if (add) next.add(id); else next.delete(id);
      return next;
    };
    setOpened((prev) => flip(prev, !open));
    setClosed((prev) => flip(prev, open));
  };

  const toggleChip = (id) => setChips((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const clearQuery = () => {
    setQuery("");
    setApplied("");
    inputRef.current?.focus();
  };

  // ⚠️ THE BELT TO THE ROUTE GUARDS' BRACES. App.js already wraps /team in
  // Protected > OnboardingGate > NotScheduler > NotBoardManager, and the
  // Firestore rules enforce the same allowlist independently — but a route
  // guard set is easy to get wrong, this page holds the whole department's
  // contact details, and the native shell mounts this component through a
  // DIFFERENT route tree where those web guards do not run at all. The check
  // lives in the shared body, not in TeamPage, so both shells inherit it.
  const allowed = canSeeTeamDirectory(auth);
  const isAdmin = !!auth?.isAdmin;

  return (
    <div style={inShell ? { ...s.body, ...s.bodyInShell } : s.body}>
      <header style={s.head}>
        <p style={s.eyebrow}>Department directory</p>
        <h1 style={s.title}>Team</h1>
        <div style={s.rule} aria-hidden="true" />
        {/* Both the provenance line and the search bar belong to a directory
            this account may actually read. A search field over a surface you
            are refused is theatre — the notice below says what happened. */}
        {allowed && (
          <p style={s.provenance}>
            The people, surgeons and facilities your hospital keeps in ATC.
            Nothing here comes from outside the app.
          </p>
        )}

        {allowed && (
          // A real search landmark. onSubmit is stopped because there is
          // nothing to submit — the list is already filtering as you type — but
          // the form is what makes Enter behave and what names the region.
          <form
            style={s.searchForm}
            role="search"
            aria-label="Search the team directory"
            onSubmit={(e) => e.preventDefault()}
          >
            <label style={s.searchLabel} htmlFor="team-search">
              Search by name, number or email
            </label>
            <div style={s.field}>
              <span style={s.fieldIcon} aria-hidden="true">🔍</span>
              {/* ⚠️ NO NAME IN THE PLACEHOLDER (client, 2026-08-19). It read
                  "Larson, 8135550142, or larson@" — a worked example meant to
                  teach the three things you can search. In a directory of 325
                  real colleagues a surname sitting in the box reads as a
                  RECORD, not as a hint: it looks like somebody already
                  searched, or like Larson is the first entry. The label above
                  the field already says "Search by name, number or email", so
                  the formats are stated where they belong and the field just
                  invites typing. */}
              <input
                id="team-search"
                ref={inputRef}
                type="search"
                style={s.input}
                value={query}
                placeholder="Start typing…"
                autoComplete="off"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") clearQuery(); }}
              />
              {query && (
                <button type="button" className="atc-team-warm" style={s.clear} onClick={clearQuery} aria-label="Clear search">
                  <span aria-hidden="true">✕</span>
                </button>
              )}
            </div>

            <div style={s.chips} role="group" aria-label="Filter by section">
              {FILTERABLE_SECTION_IDS.map((id) => {
                const on = chips.has(id);
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={on}
                    className="atc-team-warm"
                    style={on ? { ...s.chip, ...s.chipOn } : s.chip}
                    onClick={() => toggleChip(id)}
                  >
                    {/* The ✓ carries the pressed state without colour, so the
                        chips still read in greyscale and to a screen reader. */}
                    {on && <span aria-hidden="true">✓</span>}
                    {SECTION_LABELS[id]}
                  </button>
                );
              })}
            </div>

            <p style={s.count} aria-live="polite">
              {loading ? "Loading the directory…" : countLabel(shown, total)}
            </p>
          </form>
        )}
      </header>

      <div style={s.results}>
        <div style={s.resultsInner}>
          {!allowed ? (
            <Notice
              title="The directory isn't part of this account"
              body="It lists the personal contact details of everyone in the department, so it is limited to the clinicians who need to reach them — anesthesiologists, CRNAs and AAs, and administrators. Shared board and display accounts, schedulers and observers can't open it."
              ctaLabel="Open the board"
              onCta={() => navigate("/board")}
            />
          ) : error ? (
            <Notice
              title={
                String(error).includes("permission")
                  ? "The directory isn't open to this account"
                  : "The directory couldn't be loaded"
              }
              body={
                String(error).includes("permission")
                  ? "It holds the personal phone numbers and email addresses of the whole department, so it is limited to clinicians — shared board and display accounts can't open it."
                  : "The connection to the directory failed. Everything else in the app is working; try again in a moment."
              }
              ctaLabel="Open the board"
              onCta={() => navigate("/board")}
            />
          ) : loading ? null : total === 0 ? (
            // ⚠️ THIS NO LONGER MEANS "THE SYNC HAS NOT RUN". There is no sync.
            // It means nobody has been added — so an administrator, who is the
            // one person who can fix it, is told exactly where to go.
            <Notice
              title="No one has been added yet"
              body={
                isAdmin
                  ? "Add colleagues, surgeons and facility contact details in Setup → Facility, and they will appear here for everyone who can open this page."
                  : "Once an administrator adds your colleagues, surgeons and facility contact details in ATC, every number and address will be here."
              }
              ctaLabel={isAdmin ? "Open Setup" : "Back to home"}
              onCta={() => navigate(isAdmin ? "/setup?tab=facility" : "/home")}
            />
          ) : shown === 0 ? (
            <Notice
              title="Nobody matches that"
              body={
                applied.trim()
                  ? `Nothing in the directory matches “${applied.trim()}”. Try a last name on its own, the first few digits of a number, or part of an address.`
                  : "Nothing is left in the sections you have selected. Turn one back on to see them."
              }
              ctaLabel={applied.trim() ? "Clear the search" : undefined}
              onCta={clearQuery}
            />
          ) : (
            sections.map((section) => {
              const open = isOpen(section.id);
              const limit = limits[section.id] || PAGE;
              const visible = open ? section.rows.slice(0, limit) : [];
              const hidden = section.rows.length - visible.length;
              return (
                <section key={section.id} style={s.group}>
                  <h2 style={s.groupHeading}>
                    <button
                      type="button"
                      className="atc-team-warm"
                      style={s.groupToggle}
                      aria-expanded={open}
                      aria-controls={`team-list-${section.id}`}
                      onClick={() => toggleSection(section.id)}
                    >
                      <span style={s.caret} aria-hidden="true">{open ? "▼" : "►"}</span>
                      <span style={s.groupLabel}>{section.label}</span>
                      <span style={s.groupCount}>{section.rows.length}</span>
                    </button>
                  </h2>
                  {open && (
                    <ul id={`team-list-${section.id}`} style={s.list}>
                      {visible.map((row) => (
                        <ContactRow key={row.id} row={row} />
                      ))}
                    </ul>
                  )}
                  {open && hidden > 0 && (
                    <button
                      type="button"
                      className="atc-team-warm"
                      style={s.more}
                      onClick={() => setLimits((prev) => ({ ...prev, [section.id]: section.rows.length }))}
                    >
                      Show the remaining {hidden}
                    </button>
                  )}
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
